import { GripVertical } from 'lucide-react'

interface ResizeHandleProps {
  // Pointer (not mouse) so the drag works for touch + pen too
  onPointerDown: (e: React.PointerEvent) => void
  // True while THIS handle is being dragged — keeps the grip lit even after
  // the pointer moves off the thin handle onto the editor.
  dragging?: boolean
  /** Show a faint grip at rest (chat home: panels float on the open stage
   *  with no card seam to hint at the handle, so it needs its own tell).
   *  Workspace keeps the default invisible-at-rest look. */
  visibleAtRest?: boolean
}

export function ResizeHandle({ onPointerDown, dragging = false, visibleAtRest = false }: ResizeHandleProps) {
  return (
    <div
      onPointerDown={onPointerDown}
      // Full-height strip: thin at rest, widens on hover/drag (like before).
      // No z-index / negative margin — it must not overlap and steal clicks
      // from the editor edge. touch-none so a touch-drag resizes rather than
      // scrolling the page. The row's side inset lives on the panels' margins
      // (not the container's padding), so a collapsed panel takes its inset
      // with it and this handle just sits at the freed edge in normal flow.
      className={`group relative rounded flex items-center justify-center my-[15px] w-1.5 hover:w-2 shrink-0 cursor-col-resize touch-none select-none transition-all ${
        dragging ? 'w-2 bg-brand-500/10' : 'hover:bg-brand-500/20'
      }`}
    >
      {/* Touch tell. A finger has no hover to reveal the dots below, so on a
          coarse pointer the handle would be an invisible 6px line under a
          ~40px fingertip — you had to already know it was there. Show a
          standing pill instead (the iPadOS split-view divider tell) and drop
          the hover-driven dots. Coarse-pointer only: the mouse look, and the
          rule that this strip never overlaps or steals clicks from the editor
          edge, are both untouched.

          Painted in the theme's SEAM colour, not the brand purple: this is a
          standing tell on every panel edge, so it has to read as part of the
          furniture. It lifts to the muted text colour while dragging, which
          is the only moment it should draw the eye. */}
      <span
        aria-hidden
        className={`hidden pointer-coarse:block absolute h-9 w-1 rounded-full pointer-events-none transition-colors ${
          dragging ? 'bg-[var(--text-muted)]' : 'bg-[var(--border-secondary)]'
        }`}
      />
      {/* Dots grip: invisible at rest, a muted purple on hover OR while
          dragging (not hover-only). pointer-events-none so it never
          intercepts the resize cursor. */}
      <GripVertical
        size={12}
        className={`pointer-coarse:hidden pointer-events-none text-brand-300 transition-opacity ${
          dragging ? 'opacity-60' : visibleAtRest ? 'opacity-25 group-hover:opacity-60' : 'opacity-0 group-hover:opacity-60'
        }`}
      />
    </div>
  )
}
